import { useState, useCallback, useEffect, ReactElement } from 'react';
import { MediaGallerySt, MediaGalleryWrapperSt } from './MediaGallery.css';
import { useDisableBodyScroll } from 'app/hooks/useDisableBodyScroll';
import NotAvailableOverlay from 'app/shared/components/NotAvailableOverlay/NotAvailableOverlay';
import { insideEditable, MediaData, MediaType } from './utils';
import { MediaSlider } from './components/MediaSlider/MediaSlider';
import { VehicleListingData } from 'app/apiCalls/vehicle';
import { usePlayMedia } from 'app/providers/PlayVideoProvider';
import { useTrackingPageContext } from 'app/shared/components/PageWrapper/providers/TrackingPageProvider/TrackingPageProvider';
import { usePageContext } from 'app/shared/components/PageWrapper/providers/PageProvider/PageProvider';
import { Image } from 'app/shared/components/Image/Image';
import { isNewVehicle } from 'app/utils/utils';
import {
  D2C_DATASOURCE,
  DEALER_ECOMMERCE_DATASOURCE,
} from 'next-app/app/config';

enum KEY_CODES {
  ESC = 27,
  ARROW_LEFT = 37,
  ARROW_RIGHT = 39,
}

const videoPreviewSrcMap = {
  [D2C_DATASOURCE]: `https://cdn.heycar.com/images/cas/hey22002_ecommerce/preview_d2c_video.png`,
  [DEALER_ECOMMERCE_DATASOURCE]: `https://cdn.heycar.com/images/cas/hey22002_ecommerce/preview_ecommerce_video.png`,
};

const videoSrcMap = {
  [D2C_DATASOURCE]: `https://cdn.heycar.com/images/cas/hey22002_ecommerce/hey22002_ecommerce_1080p.mp4`,
  [DEALER_ECOMMERCE_DATASOURCE]: `https://cdn.heycar.com/images/cas/hey22002_ecommerce/hey23001_ecommerce_1080p.mp4`,
};

export interface IMediaGalleryProps {
  items?: MediaData[];
  disabled?: boolean;
  vehicle?: VehicleListingData;
  forceDisableFullscreen?: boolean;
  withBackground?: boolean;
}

const MediaGallery = ({
  items,
  disabled,
  vehicle,
  withBackground = false,
  forceDisableFullscreen = false,
}: IMediaGalleryProps): ReactElement => {
  if (!items || !items.length) {
    return (
      <>
        {disabled && (
          <NotAvailableOverlay withNumberOfSimilarCars vehicle={vehicle} />
        )}
        <Image
          src="/assets/images/default-car-placeholder.png"
          dataTestId="media-gallery-default-placeholder"
        />
      </>
    );
  }
  const { trackAction } = useTrackingPageContext();
  const { pageId } = usePageContext();

  const { setMediaPlayMode, fullScreenAndPlay } = usePlayMedia();
  const videoPreviewSrc = videoPreviewSrcMap[vehicle?.dataSource];
  const videoBaseUrl = videoSrcMap[vehicle?.dataSource];

  const videoList = [
    {
      mediaType: MediaType.VIDEO,
      previewSrc: videoPreviewSrc,
      src: videoBaseUrl,
      type: 'video/mp4',
    },
  ];

  const [offset, setOffset] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const last = items.length - 1;
  const lastVideo = videoList.length - 1;

  useDisableBodyScroll(fullscreen);

  const handleBackClick = useCallback(() => {
    setOffset(offset => (offset > 0 ? offset - 1 : 0));
    trackAction(`${pageId.toLowerCase()}_mlt_carousel_click_scroll_left`, {
      description: 'User clicks prev carousel button',
    });
  }, [pageId, setOffset, trackAction]);

  const handleNextClick = useCallback(() => {
    if (fullScreenAndPlay) {
      setOffset(offset => (offset < lastVideo ? offset + 1 : lastVideo));
    } else {
      setOffset(offset => (offset < last ? offset + 1 : last));
    }
    trackAction(`${pageId.toLowerCase()}_mlt_carousel_click_scroll_right`, {
      description: 'User clicks next carousel button',
    });
  }, [last, pageId, setOffset, fullScreenAndPlay, lastVideo, trackAction]);

  const handleMediaGallerySlideClick = useCallback(
    (index: number) => {
      if (!fullScreenAndPlay) {
        trackAction(
          'vehicle_carousel_click',
          {
            value: `${index}`,
            description: 'User clicks carousel',
          },
          {
            isNewVehicle: isNewVehicle(vehicle),
          },
        );
      }
      if (!forceDisableFullscreen && !fullScreenAndPlay) {
        trackAction('vehicle_carousel_fullscreen_click', {
          label: 'opening',
          description: 'User clicks fullscreen carousel button',
        });
        fullScreenAndPlay ? setFullscreen(false) : setFullscreen(true);
      }
    },
    [
      setFullscreen,
      forceDisableFullscreen,
      fullScreenAndPlay,
      trackAction,
      vehicle,
    ],
  );

  const handleMediaGalleryClick = useCallback(
    index => {
      setOffset(index);
    },
    [setOffset],
  );

  const disableFullscreen = useCallback(() => {
    if (!forceDisableFullscreen) {
      trackAction('vehicle_carousel_fullscreen_click', {
        label: 'closing',
        description: 'User clicks carousel fullscreen button',
      });
      setFullscreen(false);
      if (fullScreenAndPlay) setMediaPlayMode(false);
    }
  }, [setFullscreen, forceDisableFullscreen, setMediaPlayMode, trackAction]);

  useEffect(() => {
    const handleKeyDown = ({ keyCode, target }) => {
      if (insideEditable(target)) {
        return;
      }

      if (keyCode === KEY_CODES.ESC) {
        disableFullscreen();
      }
      if (keyCode === KEY_CODES.ARROW_LEFT) {
        handleBackClick();
      }
      if (keyCode === KEY_CODES.ARROW_RIGHT) {
        handleNextClick();
      }
    };

    setOffset(offset);

    document.body.addEventListener('keydown', handleKeyDown);

    if (fullScreenAndPlay) setOffset(0);

    return () => {
      document.body.removeEventListener('keydown', handleKeyDown);
    };
  }, [disableFullscreen, handleBackClick, handleNextClick, setOffset]);

  useEffect(() => {
    setOffset(0);
  }, [setOffset, vehicle?.id]);

  return (
    <MediaGalleryWrapperSt withBackground={withBackground}>
      {disabled && (
        <NotAvailableOverlay withNumberOfSimilarCars vehicle={vehicle} />
      )}
      <MediaGallerySt fullscreen={fullscreen} withBackground={withBackground}>
        <MediaSlider
          fullscreen={fullscreen}
          items={items}
          offset={offset}
          handleMediaGallerySlideClick={handleMediaGallerySlideClick}
          withBackground={withBackground}
          disableFullscreen={disableFullscreen}
          handleNextClick={handleNextClick}
          handleBackClick={handleBackClick}
          handleMediaGalleryClick={handleMediaGalleryClick}
          last={last}
          disabled={disabled}
        />
      </MediaGallerySt>
      {fullScreenAndPlay && (
        <MediaGallerySt
          fullscreen={fullScreenAndPlay}
          withBackground={withBackground}
        >
          <MediaSlider
            fullscreen={fullScreenAndPlay}
            items={videoList}
            offset={offset}
            handleMediaGallerySlideClick={handleMediaGallerySlideClick}
            withBackground={withBackground}
            disableFullscreen={disableFullscreen}
            handleNextClick={handleNextClick}
            handleBackClick={handleBackClick}
            handleMediaGalleryClick={handleMediaGalleryClick}
            last={lastVideo}
            disabled={disabled}
          />
        </MediaGallerySt>
      )}
    </MediaGalleryWrapperSt>
  );
};

export { MediaGallery };
